import { Flash, Grid, Column } from '@acast/decibel';
import T from 'prop-types';
import React from 'react';
import { compose } from 'redux';
import { getAudioMarker } from 'shared/helpers/markers';
import AudioFile from './audio-file';
import CoverImage from './cover-image';
import shared from './shared';
import withStatus from './with-status';

function statusForAudioFile(status) {
  switch (status) {
    case 'pending':
      return 'initializing';
    case 'encoder-failed':
    case 'upload-failed':
      return 'error';
    case 'media-file-failed':
      return 'warning';
    case 'missing':
    case 'uploading':
    case 'encoding':
      return status;
    default:
      return 'ok';
  }
}

const Media = ({ status, ...props }) => (
  <Grid>
    <Column width="50%">
      <AudioFile status={statusForAudioFile(status)} {...props} />
      {status === 'encoder-failed' && (
        <Flash intent="danger" mt={3}>
          Error encoding file.
        </Flash>
      )}
      {status === 'upload-failed' && (
        <Flash intent="danger" mt={3}>
          Error uploading audio file.
        </Flash>
      )}
      {status === 'media-file-failed' && (
        <Flash intent="warning" mt={3}>
          We're having trouble reaching the media file. Please retry.
        </Flash>
      )}
    </Column>
    <Column width="50%">
      <CoverImage />
    </Column>
  </Grid>
);

Media.propTypes = {
  status: T.string,
  fileName: T.string,
  encodedDuration: T.number,
  sourceDuration: T.number,
  uploadProgress: T.number,
  encodingStart: T.string,
  onFileReceived: T.func,
  onFileRejected: T.func,
  retry: T.func,
};

const mapProps = BaseComponent => {
  // eslint-disable-next-line react/prop-types
  const WrappedComponent = ({ markers, mediaFile, retryEncoder, ...props }) => {
    const audioMarker = getAudioMarker(markers);
    const upload = shared.getPendingUpload(audioMarker);
    const encoding = shared.getEncodingJob(markers);

    return (
      <BaseComponent
        {...props}
        fileName={shared.getAudioFilename(markers)}
        encodedDuration={mediaFile && mediaFile.data && mediaFile.data.duration}
        sourceDuration={audioMarker && audioMarker.duration}
        uploadProgress={upload && upload.progress}
        encodingStart={encoding && encoding.modified}
        retry={upload && upload.error ? undefined : retryEncoder}
      />
    );
  };

  WrappedComponent.displayName = `mapProps(${BaseComponent.displayName ||
    BaseComponent.name ||
    'Component'})`;

  return WrappedComponent;
};

const enhance = compose(
  withStatus,
  mapProps
);

export default enhance(Media);
